import { get, chain } from 'lodash';
import { RegionType, TRANSPARENCY_BREAKPOINT, upgradeColor, } from '@wix/shoutout-email-model-v3';
import { LogoType, WIX_BRAND_LOGO_URLS, WIX_BRAND_LOGO_TYPES, WIX_BRAND_LOGO_STYLE_OVERRIDES, } from '@wix/wix-emails-senders';
import classnames from 'classnames';
import { getMessageWidth } from '../../utils/get-message-width';
import * as cssUtils from '../../utils/css-utils';
import { getTextAlign } from '../../utils/get-text-align';
import { adjustImage } from '../../utils/adjust-image';
import { toRgbAsHex } from '../../utils/color';
export const getLogoSection = ({ composerData, css, region, }) => {
    if (region.type !== RegionType.Logo) {
        throw Error('Logo must be passed');
    }
    const logoStyles = getLogoStyles(region);
    const fallbackLogoSettings = composerData.styles.settings.logo;
    const backgroundSize = getBackgroundSize(logoStyles, fallbackLogoSettings);
    const messageContentWidth = getMessageWidth(composerData, css);
    const hasContentPadding = !!css['so-global-region-logo-content'];
    const userPaddings = logoStyles?.region;
    const rules = [
        logoStyles,
        userPaddings,
        css['so-global-region-logo'],
        css['so-global-region'],
    ];
    const margin = cssUtils.margin(rules);
    const padding = cssUtils.padding(rules);
    const regionPaddings = backgroundSize === 'strip'
        ? {
            margin: {
                top: 0,
                left: 0,
                right: 0,
                bottom: margin.bottom,
            },
            padding: hasContentPadding
                ? {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: padding.bottom,
                }
                : padding,
        }
        : { margin, padding };
    let stripSizeCssClass = cssUtils.getBottomPaddingRestrictionCssClass(rules);
    const visibility = get(region, 'data.visibility');
    const mobileCssClass = classnames(cssUtils.getRegionMobileCssClass(rules, false, visibility), stripSizeCssClass);
    if (composerData.styles.v1 && backgroundSize === 'strip') {
        if (margin.left) {
            stripSizeCssClass = classnames(stripSizeCssClass, 'ML16');
            regionPaddings.margin.left = margin.left;
        }
        if (margin.right) {
            stripSizeCssClass = classnames(stripSizeCssClass, 'MR16');
            regionPaddings.margin.right = margin.right;
        }
    }
    if (!hasContentPadding && backgroundSize === 'strip') {
        stripSizeCssClass = cssUtils.getRegionMobileCssClass([
            logoStyles,
            cssUtils.spacingToCssProperties(regionPaddings.margin, regionPaddings.padding),
        ], false, visibility);
    }
    const logoBackgroundColor = getBackgroundColor(region.data.styles, composerData.styles.settings.logo);
    const align = getTextAlign(logoStyles?.textAlign);
    const logoSizes = {
        small: 60,
        medium: 90,
        large: 122,
    };
    const fixedImageHeight = logoStyles?.fixedImageHeight;
    const imageSize = fixedImageHeight ?? logoSizes[logoStyles?.imageSize || 'medium'];
    const fileName = getLogoSrc(region, composerData);
    const contentPaddings = cssUtils.padding([
        css['so-global-region-logo-content'],
    ]);
    // We are doubling the image height to increase the image quality.
    // Browsers and/or email clients are handling image resize better than wix media.
    const h = imageSize * 2;
    const w = messageContentWidth -
        (regionPaddings.margin.left || 0) -
        (regionPaddings.margin.right || 0) -
        (regionPaddings.padding.left || 0) -
        (regionPaddings.padding.right || 0) -
        (contentPaddings.left || 0) -
        (contentPaddings.right || 0);
    // NOTE: Make sure that ImageWithLoader from composer still works if you change this.
    const imageProps = {
        h,
        q: 100,
        w,
        al: 'c',
        lg: 0,
    };
    const src = adjustImage(fileName, imageProps);
    const srcNonRetina = adjustImage(fileName, {
        ...imageProps,
        h: imageSize,
    });
    let columnProps;
    if (hasContentPadding) {
        columnProps = {
            innerBackgroundColor: logoBackgroundColor,
        };
    }
    else {
        columnProps = {
            backgroundColor: logoBackgroundColor,
        };
    }
    return {
        align,
        backgroundSize,
        href: region.data.imageLink,
        altText: region.data.altText,
        imageSize,
        src,
        srcNonRetina,
        outerPadding: regionPaddings.margin,
        innerPadding: regionPaddings.padding,
        columnProps,
        cssClass: backgroundSize === 'strip' ? stripSizeCssClass : mobileCssClass,
        visibility,
    };
};
function getLogoSrc(region, composerData) {
    const themeLogo = get(composerData, 'styles.settings.logo.image.url');
    const customLogo = get(region, 'data.relativeUri') || get(region, 'data.url');
    const siteLogo = get(region, 'data.siteLogoUrl');
    const logoType = get(region, 'data.logoType');
    const themeBrandMakerLogo = isBrandMakerLogo(themeLogo);
    const wixBrandLogoMap = WIX_BRAND_LOGO_TYPES.reduce((acc, logo) => {
        acc[logo] = WIX_BRAND_LOGO_URLS[logo];
        return acc;
    }, {});
    if (wixBrandLogoMap[logoType]) {
        return wixBrandLogoMap[logoType];
    }
    if (themeBrandMakerLogo) {
        return themeLogo;
    }
    if (logoType === LogoType.siteLogo && siteLogo) {
        return siteLogo;
    }
    return customLogo || themeLogo;
}
function isBrandMakerLogo(url) {
    return url?.startsWith('https://wixmp-');
}
function getBackgroundSize(styles, fallbackStyles) {
    return styles.backgroundColorEnabled
        ? styles.backgroundSize || fallbackStyles.backgroundSize || 'box'
        : 'box';
}
export function getLogoType(composerData) {
    return getBackgroundSize(composerData.regions.filter((region) => region.type === RegionType.Logo)[0]?.data.styles || {}, get(composerData, 'styles.settings.logo') || {});
}
function getBackgroundColor(styles, defaults) {
    if (styles.backgroundColorEnabled) {
        const opacity = chain([styles.opacity, defaults.opacity])
            .map((_) => parseInt(_, 10))
            .filter(Number.isInteger)
            .first()
            .defaultTo(100)
            .value();
        if (opacity > TRANSPARENCY_BREAKPOINT) {
            return (toRgbAsHex(upgradeColor(styles.backgroundColor || defaults.backgroundColor)) || 'transparent');
        }
    }
    return 'transparent';
}
export function getLogoStyles(region) {
    const logoType = region.data?.logoType;
    const customStyles = region.data?.styles;
    if (logoType && WIX_BRAND_LOGO_TYPES.includes(logoType)) {
        const styleOverrides = WIX_BRAND_LOGO_STYLE_OVERRIDES[logoType];
        if (customStyles) {
            const mergedRegion = customStyles?.region || styleOverrides?.region
                ? {
                    ...customStyles?.region,
                    ...styleOverrides?.region,
                }
                : undefined;
            return {
                ...customStyles,
                ...styleOverrides,
                ...(mergedRegion && { region: mergedRegion }),
            };
        }
        return styleOverrides;
    }
    return customStyles;
}
//# sourceMappingURL=get-logo-section.js.map